import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha, Show } from '@elfsight/lib-utils';
import { getGaugeColor } from '../utils/get-gauge-color';
import { getNumericalScore } from '../utils/get-numerical-score';

export type GaugeProps = {
  score: number;
  withAnimation?: boolean;
};

type GaugeArcProps = GaugeProps & {
  size: number;
  thickness: number;
  percentageStyle: CSSProperties;
  withBaseCircle?: boolean;
};

export function GaugeArc({
  score,
  size,
  thickness,
  withBaseCircle = false,
  withAnimation = false,
  percentageStyle
}: GaugeArcProps) {
  const color = getGaugeColor(score);

  const rawRadius = size / 2;
  const radius = rawRadius - thickness / 2;
  const circumference = 2 * Math.PI * radius;

  const numericalScore = getNumericalScore(score);

  const [style, setStyle] = useState<CSSProperties>({
    strokeDasharray: `0 ${circumference}`
  });

  useEffect(() => {
    setStyle({
      strokeDasharray: `${score * circumference} ${circumference}`
    });
  }, [score, circumference]);

  return (
    <SvgWrapper>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Show when={withBaseCircle}>
          <BaseCircle
            r={radius}
            cx={rawRadius}
            cy={rawRadius}
            strokeWidth={thickness}
          />
        </Show>
        <ArcCircle
          stroke={color}
          r={radius}
          cx={rawRadius}
          cy={rawRadius}
          strokeWidth={thickness}
          style={style}
          $withAnimation={withAnimation}
        />
      </svg>
      <Percentage $color={color} style={percentageStyle}>
        {numericalScore}
      </Percentage>
    </SvgWrapper>
  );
}

const SvgWrapper = styled.div`
  position: relative;
`;

const BaseCircle = styled.circle`
  fill: none;
  stroke: #000;
  opacity: 0.1;
`;

const ArcCircle = styled.circle<{ $withAnimation: boolean }>`
  ${({ $withAnimation }) => css`
    fill: none;
    stroke-linecap: round;
    transform-origin: 50% 50%;
    transform: rotate(-90deg);
    ${$withAnimation &&
    `
      transition:
        stroke-dasharray 2s ease,
        transform 2s ease;
    `}
  `}
`;

const Percentage = styled.div<{
  $color: string;
}>`
  ${({ $color }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    color: ${$color};
  `}
`;

export const StyledGaugeWrapper = styled.div.attrs<{
  $width: number;
  $height: number;
  $score: number;
  $backgroundOpacity: number;
}>({
  role: 'figure',
  'aria-label': 'Score value'
})`
  ${({ $width, $height, $score, $backgroundOpacity }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 50%;
    line-height: 0;
    width: ${$width}px;
    height: ${$height}px;
    background-color: ${getColorWithAlpha(
      getGaugeColor($score),
      $backgroundOpacity
    )};
  `};
`;
